import React, { useState } from 'react'
import find from 'lodash/fp/find'
import uniq from 'lodash/fp/uniq'
import Swal from 'sweetalert2'
import Button from 'react-bootstrap/Button'
import { useMutation } from '@apollo/client'
import { useHistory } from 'react-router-dom'

import { useAppSelector } from '../../../hooks'
import { FormatNumber } from '../../../components'
import { checkDuplicatedGuests } from '../../Rsvp/components'
import { IGuestPayment, Booking, BookingStatus } from '../../../types'
import {
  confirmAlert,
  errorAlert,
  loaderAlert,
  simpleAlert,
  simpleAlertWithPromise,
} from '../../../common'
import {
  ADD_ALL_ADDONS_TO_BOOKING,
  CONFIRM_BOOKING,
  DECLINE_BOOKING,
} from '../../../graphql'

import { trimGuestFields } from './bookingFunctions'
import GuestConfirmationModal from './GuestConfirmationModal'

export const GuestPayment: React.FC<IGuestPayment> = ({
  guestPrice,
  booking,
  disabled = false,
  paymentText = 'Pay',
  confirmText = 'Confirm',
}) => {
  const history = useHistory()
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [pendingAction, setPendingAction] = useState<'confirm' | 'pay' | null>(
    null
  )
  const [moveToMyGroupSelection, setMoveToMyGroupSelection] = useState<{
    [guestId: string]: boolean
  }>({})

  const {
    hidePaymentCTA,
    freeGuests = [],
    paidGuests = [],
    extraNights = [],
    addonsIdsToDelete = [],
    customBookingDate,
    customBookingDuration,
  } = useAppSelector(state => state.booking)
  const [confirmBooking, { loading }] = useMutation(CONFIRM_BOOKING)
  const [payBooking, { loading: paying }] = useMutation(
    ADD_ALL_ADDONS_TO_BOOKING
  )
  const { id: bookingId } = booking

  if (hidePaymentCTA) return null // for external validations

  // Get external guests that should trigger the confirmation modal
  const externalGuests = [
    ...freeGuests.filter(guest => guest.isExternalGuest && guest.isFromSearch),
    ...paidGuests.filter(guest => guest.isExternalGuest && guest.isFromSearch),
  ]

  const makeVariables = () => {
    const before = find({ type: 'before' }, extraNights)
    const after = find({ type: 'after' }, extraNights)
    return {
      bookingId,
      freeGuests: freeGuests.map(fg => ({
        bookingId,
        guestBookingInput: trimGuestFields(
          {
            ...fg.guest,
            moveToMyGroup: moveToMyGroupSelection[fg.id] || false,
            isFromSearch: fg.isFromSearch || false,
          },
          fg.id
        ),
        offeringAddOnId: fg.addon.id,
        bookingAddOnId: fg.bookingAddOnId,
      })),
      paidGuests: paidGuests.map(pg => ({
        bookingId,
        guestBookingInput: trimGuestFields(
          {
            ...pg.guest,
            moveToMyGroup: moveToMyGroupSelection[pg.id] || false,
            isFromSearch: pg.isFromSearch || false,
          },
          pg.id
        ),
        offeringAddOnId: pg.option.offeringAddOnId,
        offeringAddOnOptionId: pg.option.id,
        bookingAddOnId: pg.bookingAddOnId,
      })),
      extraNights: {
        bookingId,
        date: customBookingDate,
        duration: customBookingDuration,
        addonNightsAfterId: after?.offeringAddOnId,
        optionNightsAfterId: after?.id,
        addonNightsBeforeId: before?.offeringAddOnId,
        optionNightsBeforeId: before?.id,
      },
      addonsToDelete: uniq(addonsIdsToDelete),
    }
  }

  const executeConfirm = () => {
    if (checkDuplicatedGuests([...freeGuests, ...paidGuests])) {
      simpleAlert({
        html: 'There cannot be duplicated guests',
        icon: 'warning',
      })
      return
    }

    if (loading) return
    loaderAlert({ html: 'confirming...' })
    const variables = makeVariables()

    confirmBooking({ variables })
      .then(({ data }) => {
        const pay = data?.confirmBooking
        Swal.close()

        if (pay?.errors.length > 0) {
          errorAlert(
            pay?.errors,
            'there has been an error confirming your reservation'
          )
        } else {
          history.push(`/booking/${bookingId}/details`)
        }
      })
      .catch(() => {
        simpleAlert({
          html: 'There has been an error confirming your booking',
          icon: 'error',
        })
      })
  }

  const executePay = () => {
    if (checkDuplicatedGuests([...freeGuests, ...paidGuests])) {
      simpleAlert({
        html: 'There cannot be duplicated guests',
        icon: 'warning',
      })
      return
    }

    if (paying) return
    loaderAlert({ html: 'preparing...' })
    const variables = makeVariables()

    payBooking({ variables })
      .then(({ data }) => {
        const resp = data?.addAllAddonsToBooking
        Swal.close()

        if (resp?.errors.length > 0) {
          errorAlert(resp?.errors, 'there has been an error sending your data')
        } else {
          history.push(`/booking/${bookingId}/payment`)
        }
      })
      .catch(() => {
        simpleAlert({
          html: 'There has been an error confirming your booking',
          icon: 'error',
        })
      })
  }

  const checkForExternalGuestsAndProceed = (action: 'confirm' | 'pay') => {
    if (externalGuests.length > 0) {
      setPendingAction(action)
      setShowConfirmationModal(true)
    } else if (action === 'confirm') {
      executeConfirm()
    } else if (action === 'pay') {
      executePay()
    }
  }

  const handleGuestSelectionChange = (guestId: string, selected: boolean) => {
    setMoveToMyGroupSelection(prev => ({
      ...prev,
      [guestId]: selected,
    }))
  }

  const handleConfirmationComplete = () => {
    // Capture the pending action before resetting state
    const actionToExecute = pendingAction

    setShowConfirmationModal(false)
    setPendingAction(null)

    // Execute action immediately since selection is already updated
    if (actionToExecute === 'confirm') {
      executeConfirm()
    } else if (actionToExecute === 'pay') {
      executePay()
    }
  }

  const confirm = (ev: React.MouseEvent) => {
    ev.preventDefault()
    checkForExternalGuestsAndProceed('confirm')
  }

  const pay = (ev: React.MouseEvent) => {
    ev.preventDefault()
    checkForExternalGuestsAndProceed('pay')
  }

  if (guestPrice <= 0) {
    return (
      <>
        <Button className="px-3 mr-2" onClick={confirm} disabled={disabled}>
          {confirmText}
        </Button>

        <GuestConfirmationModal
          show={showConfirmationModal}
          onHide={() => {
            setShowConfirmationModal(false)
            setPendingAction(null)
          }}
          onContinue={handleConfirmationComplete}
          onGuestSelectionChange={handleGuestSelectionChange}
          externalGuests={externalGuests}
        />
      </>
    )
  }

  return (
    <>
      <div className="d-flex flex-column-reverse align-items-end flex-wrap">
        <p className="my-2">
          Total: <FormatNumber n={guestPrice} />
        </p>
        <Button disabled={disabled} className="px-5 rounded-pill" onClick={pay}>
          {paymentText}
        </Button>
      </div>

      <GuestConfirmationModal
        show={showConfirmationModal}
        onHide={() => {
          setShowConfirmationModal(false)
          setPendingAction(null)
        }}
        onContinue={handleConfirmationComplete}
        onGuestSelectionChange={handleGuestSelectionChange}
        externalGuests={externalGuests}
      />
    </>
  )
}

export const DeclineRSVP: React.FC<{ booking: Booking }> = ({ booking }) => {
  const history = useHistory()
  const [decline, { loading }] = useMutation(DECLINE_BOOKING)
  const { freeGuests = [], addonsIdsToDelete = [] } = useAppSelector(
    state => state.booking
  )

  const handleClick = (ev: React.MouseEvent) => {
    ev.preventDefault()
    if (loading) return

    // Validate that freeGuests list is not empty
    if (freeGuests.length === 0) {
      simpleAlert({
        html: 'You need to have at least one guest to decline the RSVP',
        icon: 'warning',
      })
      return
    }

    confirmAlert({
      title: 'Decline RSVP',
      html: `Are you sure you'd like to decline this RSVP?`,
    }).then(resp => {
      if (!resp.value) return

      loaderAlert({ html: 'declining...' })

      const variables = {
        id: booking.id,
        freeGuests: freeGuests.map(fg => ({
          bookingId: booking.id,
          guestBookingInput: trimGuestFields(
            {
              ...fg.guest,
              isFromSearch: fg.isFromSearch || false,
            },
            fg.id
          ),
          offeringAddOnId: fg.addon.id,
          bookingAddOnId: fg.bookingAddOnId,
        })),
        addonsToDelete: uniq(addonsIdsToDelete),
      }

      decline({ variables })
        .then(({ data }) => {
          const errors = data?.declineBooking?.errors || []
          if (errors.length > 0) {
            errorAlert(
              errors,
              'there has been an error declining your reservation'
            )
            return
          }

          simpleAlertWithPromise({
            html: 'Your RSVP has been successfully cancelled.',
            icon: 'success',
          }).then(() => {
            history.go(0)
          })
        })
        .catch(() => {
          simpleAlert({
            html: "it wasn't possible to cancel this RSVP. Please try again later.",
            icon: 'error',
          })
        })
    })
  }

  if (booking.status !== BookingStatus.Created) return null
  return (
    <Button className="px-3" onClick={handleClick} variant="outline-secondary">
      Decline
    </Button>
  )
}
